/**
 * Extracts route parameters from a route string.
 * @template R - The route string.
 */
type ExtractRouteParams<R extends string> =
  R extends `${string}:${infer Param}/${infer Rest}`
    ? Param | ExtractRouteParams<`/${Rest}`>
    : R extends `${string}:${infer Param}`
      ? Param
      : never;

/**
 * Defines the parameters for a route.
 * @template T - The route string.
 */
type Params<T extends string> =
  ExtractRouteParams<T> extends never
    ? undefined
    : Required<{
        [K in ExtractRouteParams<T>]: string | number | undefined;
      }>;

/**
 * Defines the route parameters.
 * @template T - The route string.
 */
type RouteParams<T extends string> =
  Params<T> extends undefined ? undefined : Params<T>;

/**
 * Builds a route string by replacing placeholders with actual values.
 * @function buildRoute
 * @template T - The route string.
 * @param {T} route - The route template string.
 * @param {object} params - The parameters to replace in the route.
 * @param {object} [params.queryParams] - The query parameters to append to the route.
 * @param {boolean} [qualified=false] - Whether to include the domain in the route.
 * @returns {string} The built route string.
 */
const buildRoute = <T extends string>(
  route: T,
  params: {
    [K in ExtractRouteParams<T>]: string | number | undefined;
  } & {
    queryParams?: Record<string, string | number | undefined>;
  },
  qualified?: boolean,
): string => {
  // Separate path parameters and query parameters
  const { queryParams, ...pathParams } = params;

  // Replace placeholders in the route
  let builtRoute = route.replace(/:([a-zA-Z0-9_]+)/g, (_, key) => {
    if (key in pathParams) {
      return encodeURIComponent(
        String(pathParams[key as keyof typeof pathParams]),
      );
    }
    throw new Error(`Missing parameter: ${key}`);
  });

  if (qualified) {
    const domain = `${window.location.protocol}//${window.location.host}`;
    builtRoute = `${domain}${builtRoute}`;
  }

  // Handle query parameters
  if (queryParams) {
    const queryString = new URLSearchParams(
      Object.entries(queryParams).map(([key, value]) => [key, String(value)]),
    ).toString();
    return `${builtRoute}?${queryString}`;
  }

  return builtRoute;
};

export { buildRoute };
export type { ExtractRouteParams, RouteParams };
